import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Badge } from '../components/ui/Badge';
import { SearchInput } from '../components/ui/SearchInput';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { CountBand } from '../components/ui/CountBand';
import { apiList } from '../api/apiClient';
import { VoiceSettings } from './VoiceSettings';
import { RefreshCw } from 'lucide-react';
import { visitorOrigin } from '../utils/surface';

export const Intercoms: React.FC = () => {
  return (
    <div className="shadow-list-page prototype-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Intercoms</h1>
        <p className="text-gray-500 mt-1">Intercom systems across all properties</p>
      </div>

      {/* Voice + Video sub-tabs retired — the overview table already covers
          both. Phone numbers are now assigned on each property's Intercoms
          tab (Devices modal). The renderers below stay imported (VoiceSettings)
          in case we re-add them later, but no UI route renders them now. */}

      <IntercomsOverviewTab />
    </div>
  );
};

type IntercomType = 'video_only' | 'voice_video' | 'voice_only';

// One row per intercom configuration (a property may have many).
interface IntercomRow {
  // Identity
  id: string;                 // intercom_config uuid
  property_id: string;
  property_name: string;
  property_address: string;
  customer_name: string;
  // Per-intercom
  label: string;
  phone_number: string;       // dedicated intercom #, may differ from property.telnyx_number
  intercom_type: IntercomType;
  kiosk_code: string | null;
  kiosk_activated: boolean;
  visitor_url: string;
  // Per-intercom metrics — currently only available when the call/page-view
  // recorded the intercom_config_id. Falls back to property aggregate.
  voice_calls: number;
  video_calls: number;
  voice_minutes: number;
  video_minutes: number;
  door_unlocks: number;
}

const IntercomsOverviewTab: React.FC<{ videoOnly?: boolean }> = ({ videoOnly = false }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<IntercomRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | IntercomType>('all');
  const [generating, setGenerating] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [addPropertyId, setAddPropertyId] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      // properties + customers come back RBAC-scoped to the logged-in user.
      // intercom_configs and call_events are then scoped to that property set.
      const [properties, customers, intercomConfigs, calls] = await Promise.all([
        apiList('properties').catch(() => []),
        apiList('customers').catch(() => []),
        apiList('intercom_configs').catch(() => []),
        apiList('call_history').catch(() => []),
      ]);

      const props: any[] = Array.isArray(properties) ? properties : [];
      const propById = new Map<string, any>();
      const canonicalPropertyId = new Map<string, string>();
      props.forEach((p: any) => {
        const canonical = String(p.uuid || p.property_uuid || p.id);
        [p.id, p.uuid, p.property_uuid, p.legacy_id].filter(Boolean).forEach((ref) => {
          propById.set(String(ref), p);
          canonicalPropertyId.set(String(ref), canonical);
        });
      });
      const custName = new Map((Array.isArray(customers) ? customers : []).map((c: any) => [String(c.id), c.company || c.company_name || c.name || '']));

      // Aggregate by both property_id and intercom_config_id. Per-intercom
      // values win when the row carries an intercom_config_id; otherwise the
      // property aggregate is the fallback (legacy rows + non-attributed kiosk).
      const voiceCalls = new Map<string, number>();
      const videoCalls = new Map<string, number>();
      const voiceMins = new Map<string, number>();
      const videoMins = new Map<string, number>();
      const voiceCallsByIc = new Map<string, number>();
      const videoCallsByIc = new Map<string, number>();
      const voiceMinsByIc = new Map<string, number>();
      const videoMinsByIc = new Map<string, number>();
      for (const c of calls as any[]) {
        const pid = String(c.property_id);
        const ic = c.intercom_config_id ? String(c.intercom_config_id) : null;
        const dur = Number(c.duration ?? c.duration_seconds ?? 0);
        const minutes = Math.round((dur / 60) * 100) / 100;
        const type = String(c.call_type || '').toLowerCase();
        const isVideo = type === 'video';
        const isVoice = type === 'phone' || type === 'voice' || type === 'intercom';
        if (isVideo) {
          videoCalls.set(pid, (videoCalls.get(pid) || 0) + 1);
          videoMins.set(pid, (videoMins.get(pid) || 0) + minutes);
          if (ic) {
            videoCallsByIc.set(ic, (videoCallsByIc.get(ic) || 0) + 1);
            videoMinsByIc.set(ic, (videoMinsByIc.get(ic) || 0) + minutes);
          }
        } else if (isVoice) {
          voiceCalls.set(pid, (voiceCalls.get(pid) || 0) + 1);
          voiceMins.set(pid, (voiceMins.get(pid) || 0) + minutes);
          if (ic) {
            voiceCallsByIc.set(ic, (voiceCallsByIc.get(ic) || 0) + 1);
            voiceMinsByIc.set(ic, (voiceMinsByIc.get(ic) || 0) + minutes);
          }
        }
      }

      const explicitConfigs = (Array.isArray(intercomConfigs) ? intercomConfigs : [])
        .filter((c: any) => propById.has(String(c.property_id)));

      // Inventory is persisted intercom_config rows only. A property's public
      // visitor directory is a route, not an intercom device or kiosk record.
      const rows: IntercomRow[] = explicitConfigs
        .map((c: any) => {
          const prop = propById.get(String(c.property_id)) as any;
          // Each intercom owns its own phone number — properties with multiple
          // voice intercoms have multiple numbers. We never substitute the
          // property's number for an intercom's; if the intercom has none, it
          // displays blank and is treated as video-only.
          const phone = (c.phone_number || '').toString().trim();
          // Phone presence is the single source of truth for type. The legacy
          // `mode` field defaults to 'video' on every row regardless of phone,
          // so trusting it would mislabel every voice-capable intercom.
          const intercomType: IntercomType = phone ? 'voice_video' : 'video_only';
          // Use property-level lifetime counters from the canonical postgres
          // columns. All-time usage matches what users see on Stripe / billing
          // and avoids the "where did Q1's calls go?" confusion that MTD causes.
          const pid = canonicalPropertyId.get(String(c.property_id)) || String(c.property_id);
          const propVoice = Number(prop?.voice_minutes_lifetime ?? prop?.voice_minutes_this_month ?? 0);
          const propVideo = Number(prop?.video_minutes_lifetime ?? prop?.video_minutes_this_month ?? 0);
          const propDoorUnlocks = Number(prop?.door_unlocks_lifetime ?? prop?.door_unlocks_this_month ?? 0);
          const intercomId = String(c.id || c.uuid);
          const visitorBase = visitorOrigin().replace(/\/$/, '');
          // Persisted intercoms always use their scoped visitor route so the
          // correct greeting, units, calls, and doors load for that intercom.
          const visitorUrl = `${visitorBase}/${pid}/i/${intercomId}`;
          return {
            id: intercomId,
            property_id: pid,
            property_name: prop?.name || '',
            property_address: [prop?.address, prop?.city, prop?.state].filter(Boolean).join(', '),
            customer_name: custName.get(String(prop?.customer_id)) || '',
            label: c.label || '',
            phone_number: phone,
            intercom_type: intercomType,
            kiosk_code: c.kiosk_code || null,
            kiosk_activated: !!c.kiosk_activated_at,
            visitor_url: visitorUrl,
            // Video-only intercoms can't receive voice calls — zero those out
            // even if a stray legacy row was misattributed.
            voice_calls: intercomType === 'video_only' ? 0 : (voiceCallsByIc.get(intercomId) ?? voiceCalls.get(pid) ?? 0),
            video_calls: videoCallsByIc.get(intercomId) ?? videoCalls.get(pid) ?? 0,
            voice_minutes: intercomType === 'video_only' ? 0 : (voiceMinsByIc.get(intercomId) ?? propVoice ?? (voiceMins.get(pid) || 0)),
            video_minutes: videoMinsByIc.get(intercomId) ?? propVideo ?? (videoMins.get(pid) || 0),
            door_unlocks: propDoorUnlocks,
          };
        });

      setData(rows);
    } catch (e) {
      console.error('Failed to load intercom data:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    let result = data;
    if (videoOnly) {
      result = result.filter(d => d.video_calls > 0 || d.intercom_type === 'video_only' || d.intercom_type === 'voice_video');
    }
    if (typeFilter !== 'all') {
      result = result.filter(d => d.intercom_type === typeFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(d =>
        d.property_name.toLowerCase().includes(q) ||
        d.label.toLowerCase().includes(q) ||
        d.property_address.toLowerCase().includes(q) ||
        d.customer_name.toLowerCase().includes(q) ||
        (d.phone_number || '').includes(q)
      );
    }
    return result;
  }, [data, search, typeFilter, videoOnly]);

  const getIntercomBadge = (type: IntercomType) => {
    switch (type) {
      case 'voice_video': return <Badge color="purple">Voice + Video</Badge>;
      case 'video_only': return <Badge color="blue">Video Only</Badge>;
      case 'voice_only': return <Badge color="yellow">Voice Only</Badge>;
    }
  };

  const copyDirectoryLink = async (row: IntercomRow) => {
    await navigator.clipboard.writeText(row.visitor_url);
    setCopiedId(row.id);
    window.setTimeout(() => setCopiedId((current) => current === row.id ? null : current), 1800);
  };

  const handleGenerateKiosk = async (intercomId: string) => {
    setGenerating(intercomId);
    try {
      const API = process.env.REACT_APP_API_URL || 'https://shadow.api.intrlock.io';
      const token = localStorage.getItem('intrlock_token') || '';
      const res = await fetch(`${API}/intercom-configs/${intercomId}/regenerate-kiosk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const out = await res.json();
      if (!res.ok) throw new Error(out.error || 'Failed to generate code');
      alert(`Kiosk activation code: ${out.kiosk_code}`);
      await reload();
    } catch (e: any) {
      alert(`Failed to generate kiosk code: ${e.message}`);
    } finally {
      setGenerating(null);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;

  const formatMins = (m: number) => `${Math.round(m).toLocaleString()}m`;
  const propertyOptions = Array.from(new Map(data.map((row) => [row.property_id, row.property_name])).entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="shadow-list-page intercom-overview-list">
      <div className="prototype-toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Search intercoms..." />
        <label>
          <small>Mode</small>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as 'all' | IntercomType)}>
            <option value="all">All</option>
            <option value="voice_video">Voice + Video</option>
            <option value="video_only">Video Only</option>
          </select>
        </label>
        <button className="prototype-refresh" onClick={reload} aria-label="Refresh intercoms">
          <RefreshCw /> <span>Refresh</span>
        </button>
        <Button onClick={() => setShowAdd(true)}>Add Intercom</Button>
      </div>

      <CountBand value={filtered.length} detail="Intercoms in the current accessible portfolio" />

      <div className="intercom-standard-grid grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
        {filtered.length === 0 ? (
          <div className="prototype-empty md:col-span-2 xl:col-span-3">No intercoms found.</div>
        ) : filtered.map((d) => (
          <article key={d.id} className="customer-grid-card intercom-grid-card bg-white border border-gray-200">
            <header className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <h3 className="truncate"><a href={d.visitor_url} target="_blank" rel="noreferrer">{d.label || 'Intercom'}</a></h3>
                <p className="text-xs text-gray-500 truncate">{d.property_name || 'Property unavailable'}</p>
              </div>
              {getIntercomBadge(d.intercom_type)}
            </header>
            <div className="customer-card-address"><span>Address</span><p>{d.property_address || 'No address on file'}</p></div>
            <div className="customer-card-contact">
              <span>System details</span>
              <strong>{d.customer_name || 'Customer unavailable'}</strong>
              {d.phone_number ? <a href={`tel:${d.phone_number}`}>{d.phone_number}</a> : <p>Video only · no phone number</p>}
            </div>
            <details className="customer-card-metrics" onClick={(e) => e.stopPropagation()}>
              <summary><span className="show-label">Show metrics</span><span className="hide-label">Hide metrics</span><b aria-hidden="true">⌄</b></summary>
              <div>
                <article><span>Voice calls</span><strong>{d.voice_calls}</strong></article>
                <article><span>Video calls</span><strong>{d.video_calls}</strong></article>
                <article><span>Voice time</span><strong>{formatMins(d.voice_minutes)}</strong></article>
                <article><span>Video time</span><strong>{formatMins(d.video_minutes)}</strong></article>
                <article><span>Unlocks</span><strong>{d.door_unlocks}</strong></article>
                <article><span>Kiosk</span><strong>{d.kiosk_activated ? 'Active' : 'Inactive'}</strong></article>
              </div>
            </details>
            <footer className="intercom-card-actions flex items-center flex-wrap gap-3 py-3">
              <a href={d.visitor_url} target="_blank" rel="noreferrer" className="directory-action">Open Visitor Page</a>
              <button onClick={() => void copyDirectoryLink(d)} className="directory-action">{copiedId === d.id ? 'Copied' : 'Copy Visitor Link'}</button>
              <button onClick={() => handleGenerateKiosk(d.id)} disabled={generating === d.id} className="directory-action">{generating === d.id ? 'Working…' : d.kiosk_code ? 'Regenerate Kiosk' : 'Generate Kiosk'}</button>
              <button onClick={() => navigate(`/properties/${d.property_id}`)} className="row-open">Open <span aria-hidden="true">→</span></button>
            </footer>
          </article>
        ))}
      </div>
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Intercom">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Choose the property where the intercom will be configured.</p>
          <select className="w-full border border-gray-300 px-3 py-2" value={addPropertyId} onChange={(e) => setAddPropertyId(e.target.value)}>
            <option value="">Select a property</option>
            {propertyOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button disabled={!addPropertyId} onClick={() => navigate(`/properties/${addPropertyId}?tab=intercoms`)}>Continue</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
